import type { LlmLogDto } from '../types/engineering';

const BASE = `${import.meta.env.APP_BASE_API_URL}/api/llm-logs`;

/**
 * Retrieves the Bearer token from sessionStorage
 */
function getAuthToken(): string | null {
  const token = sessionStorage.getItem('msal.active.token');
  if (!token) return null;
  
  // Remove aspas duplas se o token estiver envolvido com elas
  return token.replace(/^"|"$/g, '');
}

/**
 * Parses SSE events from text stream
 */
function parseSSEEvent(text: string): { event: string; data: string } | null {
  const lines = text.split('\n');
  let event = '';
  let data = '';

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.substring(6).trim();
    } else if (line.startsWith('data:')) {
      data = line.substring(5).trim();
    }
  }

  // Must have both event AND data to be valid
  return event && data ? { event, data } : null;
}

export function streamPlannerLogs(
  executionId: string,
  onMessage: (log: LlmLogDto) => void,
  onDone: () => void,
  onError: (err: Event) => void,
  agent: string = 'PLANNER',
): () => void {
  const url = `${BASE}/stream?executionId=${executionId}&agent=${agent}`;
  const token = getAuthToken();
  const abortController = new AbortController();

  // Use fetch to support Authorization header (EventSource doesn't support custom headers)
  fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'text/event-stream',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
    signal: abortController.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          const events = buffer.split('\n\n');
          
          // Keep the last incomplete event in buffer
          buffer = events.pop() || '';

          for (const eventText of events) {
            if (!eventText.trim()) continue;

            const parsed = parseSSEEvent(eventText);
            if (!parsed) continue;

            if (parsed.event === 'llm-log' && parsed.data) {
              try {
                const log: LlmLogDto = JSON.parse(parsed.data);
                onMessage(log);
              } catch {
                // ignore malformed events
              }
            } else if (parsed.event === 'stream-end') {
              reader.cancel();
              onDone();
              return;
            }
          }
        }

        onDone();
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          onError(new Event('error'));
        }
      }
    })
    .catch((error) => {
      if (error.name !== 'AbortError') {
        console.error('SSE connection error:', error);
        onError(new Event('error'));
      }
    });

  return () => abortController.abort();
}
